/**
 * Type definitions for Table components
 */

import { UserStatus } from '@/data/users/User';
import { Theme as MuiTheme, SxProps } from '@mui/material';
import {
  GridCallbackDetails,
  GridColDef,
  GridFilterModel,
  GridGroupingColDefOverride,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridTreeNodeWithRender,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { Theme } from 'klayowebshared';
import type { ReactNode, SyntheticEvent } from 'react';

// ============================================================================
// Base Row Types
// ============================================================================

/** Base interface for table row data */
export interface BaseTableRow extends GridValidRowModel {
  id?: string | number;
  isParent?: boolean;
  isChild?: boolean;
  [key: string]: unknown;
}

// ============================================================================
// Table Column Types
// ============================================================================

/** Extended column definition with visibility breakpoints */
export interface TableColumnDef<TRow extends BaseTableRow = BaseTableRow> extends Omit<
  GridColDef<TRow>,
  'field' | 'type' | 'width'
> {
  field?: string;
  type?:
    | string
    | {
        field?: string;
        label?: string;
        visibleFromBreakpoint?: string;
        visibleToBreakpoint?: string;
        [key: string]: unknown;
      };
  label?: string;
  visibleFromBreakpoint?: string;
  visibleToBreakpoint?: string;
  numRows?: number;
  rowHeight?: string | number;
  width?: number | string;
  // Additional DataGrid properties
  getActions?: (params: GridRowParams<TRow>) => ReactNode[];
  disableColumnFilter?: boolean;
  // Additional display properties
  visibleHeader?: boolean;
  visibleIcon?: boolean;
  showRole?: boolean;
  tooltip?: string;
  // Group-related properties for employee tables
  groupLevelTypes?: Array<{ id: number | string; label: string; name: string }>;
  groupNotificationTypes?: Array<{ id: number | string; name: string }>;
  // Additional styling
  className?: string;
  showJobMatchingIcon?: boolean;
  selectable?: boolean;
  selectableOnChild?: boolean;
  blur?: boolean;
  enableChip?: boolean;
  onClick?: (event: SyntheticEvent, row: TRow) => void;
  getRowId?: (params: GridRowParams<TRow>) => string | number;
}

// ============================================================================
// Table Props
// ============================================================================

/** Props for the base Table component */
export interface TableProps<TRow extends BaseTableRow = BaseTableRow> {
  // Data
  rows?: TRow[] | null;
  columns?: TableColumnDef<TRow>[];
  totalCount?: number;
  rowCount?: number;
  getRowId?: (row: TRow) => string | number;

  // Pagination
  pagination?: boolean;
  pageSize?: number;
  paginationMode?: 'client' | 'server';
  paginationModel?: GridPaginationModel;
  rowsPerPageOptions?: number[];
  isPagination?: boolean;
  onPageChange?: (page: number, pageSize: number) => void;
  onPageSizeChange?: (pageSize: number, mode: string) => void;
  onPaginationModelChange?: (model: GridPaginationModel, details: GridCallbackDetails) => void;

  // Sorting
  sortable?: boolean;
  sortModel?: GridSortModel;
  sortingMode?: 'client' | 'server';
  onSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void;

  // Filtering
  filterMode?: 'client' | 'server';
  filterItems?: GridFilterModel['items'];
  filterOperator?: 'and' | 'or';

  // Selection
  selectable?: boolean;
  disableSelection?: boolean;
  checkboxSelection?: boolean;
  isSingleSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel, details: GridCallbackDetails) => void;
  isRowSelectable?: (params: GridRowParams<TRow>) => boolean;
  rowHasSelect?: (row: TRow) => boolean;

  // Tree/Grouping
  tree?: boolean;
  getTreeDataPath?: (row: TRow) => string[];
  groupingColDef?: GridGroupingColDefOverride<TRow>;
  expandedRowIds?: string[];
  onExpandRowChange?: (expandedIds: string[]) => void;

  // Display
  density?: 'standard' | 'comfortable' | 'compact';
  autoHeight?: boolean;
  minHeight?: number | string;
  rowHeight?: number;
  showHeader?: boolean;
  showHeaderFromBreakpoint?: string | null;
  showFooter?: boolean;
  showFooterRowCount?: boolean;
  paper?: boolean;
  dense?: boolean;
  hideFirstLastBorder?: boolean;

  // Loading/Empty states
  isLoading?: boolean;
  noRowsMessage?:
    | string
    | {
        primary?: string;
        secondary?: string;
      };
  noRowsComponent?: React.ReactNode;
  noResultsMessage?:
    | string
    | {
        primary?: string;
        secondary?: string;
      };
  noResultsComponent?: React.ReactNode;
  searchText?: string;
  keyword?: string | null;

  // Styling
  sx?: SxProps<MuiTheme>;
  tableSx?: SxProps<MuiTheme>;
  className?: string;
  icon?: React.ReactNode;

  // Content
  title?: string;
  titleRightSide?: React.ReactNode;

  // State
  disabled?: boolean;
  readOnly?: boolean;
  highlightRow?: string | number | null;
  onHighlightRowComplete?: (id: GridRowId) => void;

  // Theme
  theme?: typeof Theme;

  // Slots
  slots?: Record<string, React.ComponentType<unknown>>;

  // Event handlers
  onRowClick?: (params: GridRowParams<TRow>, event: SyntheticEvent) => void;
  onUserClick?: (event: SyntheticEvent, user: unknown, attr?: unknown) => void;

  // Component-specific props (passed through to subclasses)
  // Note: These are used by various table subclasses
  rowHasAction?: ((row: TRow) => boolean) | boolean | null;
  onDocumentSelect?: (event: SyntheticEvent, document: TRow) => void;
  onDocumentAction?: (event: SyntheticEvent, document: TRow) => void;
  onDeleteDocument?: (event: SyntheticEvent, document: TRow) => void;
  onAttributeSelect?: (event: SyntheticEvent, attribute: TRow) => void;
  onAttributeAction?: (event: SyntheticEvent, attribute: TRow) => void;
  onActionMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: TRow) => void;
  onEmployeeSelect?: (event: SyntheticEvent, employee: TRow) => void;
  onSort?: (
    model: GridSortModel,
    details: GridCallbackDetails,
    sortOrder?: unknown,
    event?: SyntheticEvent
  ) => void;

  // Address table props
  onAddressAction?: (event: SyntheticEvent, address: TRow) => void;

  // Billing history table props
  onClickDownloadLink?: (event: SyntheticEvent, row: TRow) => void;

  // Category table props
  onCategorySelect?: (event: SyntheticEvent, category: TRow) => void;
  onCategoryAction?: (event: SyntheticEvent, category: TRow) => void;

  // Training table props
  onTrainingSelect?: (event: SyntheticEvent, training: TRow) => void;
  onTrainingAction?: (event: SyntheticEvent, training: TRow) => void;
  rowNoActionIcon?: React.ReactNode;
  rowActionDisabled?: boolean | ((row: TRow) => boolean);

  // Feedback table props
  onFeedbackSelect?: (event: SyntheticEvent, feedback: TRow) => void;
  showProficiency?: boolean;
  onReviewLinkClick?: (event: SyntheticEvent, row: TRow) => void;

  // Groups table props
  onGroupSelect?: (event: SyntheticEvent, group: TRow) => void;
  onGroupAction?: (event: SyntheticEvent, group: TRow) => void;

  // Practical table props
  onPracticalSelect?: (event: SyntheticEvent, practical: TRow) => void;
  onPracticalsAction?: (event: SyntheticEvent, practical: TRow) => void;
  onDeleteParticipantAction?: (event: SyntheticEvent, participant: TRow) => void;
  isAssessment?: boolean;
  practicalStatus?: number | string;

  // Job table props
  onJobSelect?: (event: SyntheticEvent, job: TRow) => void;

  // Location table props
  onLocationAction?: (event: SyntheticEvent, location: TRow) => void;

  // Participant table props
  onParticipantSelect?: (event: SyntheticEvent, participant: TRow) => void;

  // Vacancy table props
  onVacancySelect?: (event: SyntheticEvent, vacancy: TRow) => void;
  onSave?: (event: SyntheticEvent, data: TRow) => void;
  position?: unknown;
  selected?: unknown;

  // HOC props (from withLocale)
  translate?: (key: string, options?: Record<string, unknown>) => string;
}

/** State for the base Table component */
export interface TableState {
  sortModel?: GridSortModel;
  selectionModel: GridRowSelectionModel;
  expandedRowIds: string[];
  rowsPerPageOptions?: number[];
}

// ============================================================================
// Column Bounds Types
// ============================================================================

/** Relative bounds for column positioning */
export interface ColumnBounds {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

// ============================================================================
// DataGrid API Types
// ============================================================================

/** Simplified type for DataGrid API ref */
export interface DataGridApiRef {
  current: {
    getRowElement?: (id: GridRowId) => HTMLElement | null;
    getRowNode?: (id: GridRowId) => unknown;
    getRowIndexRelativeToVisibleRows?: (id: GridRowId) => number;
    getColumnHeaderElement?: (field: string) => HTMLElement | null;
    subscribeEvent?: (event: string, handler: (node: unknown) => void) => void;
    [key: string]: unknown;
  };
}

// ============================================================================
// Attribute Table Types
// ============================================================================

/** Employee reference for attribute rows */
export interface AttributeEmployee {
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  profilePictureUrl?: string;
}

/** Category reference for attribute rows */
export interface AttributeCategory {
  id?: string;
  name?: string;
}

/** Validation request state object */
export interface ValidationRequestState {
  label?: string;
  longLabel?: string;
  value?: number;
  translationKey?: string;
  longLabelTranslationKey?: string;
  index?: number;
}

/** Attribute data for AttributeTable */
export interface AttributeTableRow extends BaseTableRow {
  // Identifiers
  attributeDefinitionId?: string;
  employeeAttributeId?: string;
  rowId?: string;

  // Names and descriptions
  name?: string;
  attribute?: string;
  attributeDefinitionName?: string;
  description?: string;

  // Category
  categoryId?: string;
  categoryName?: string;
  categories?: AttributeCategory[];

  // Proficiency
  proficiency?: number | null;
  proficiencyValue?: number | null;
  requiredProficiency?: number | null;
  currentProficiency?: number | null;
  averageProficiency?: number | null;
  validated?: boolean;

  // Dates
  expiryDate?: string | null;
  requestedExpiryDate?: string | null;
  lastUpdate?: string | null;

  // Status and type
  status?: number | { index: number; label: string; longLabel?: string; description?: string };
  statusLabel?: string;
  type?: string;
  requirementType?: string;
  reasonLabel?: string;

  // Validation request
  validationRequestState?: ValidationRequestState;
  originalValidationRequestState?: ValidationRequestState;
  hasValidationRequest?: () => boolean;
  hasRequiredDetails?: boolean;

  // Hierarchy
  isParent?: boolean;
  isChild?: boolean;
  childAttributes?: AttributeTableRow[];
  attributeHierarchy?: string[];
  isMinimum?: boolean;

  // Employees
  employees?: AttributeEmployee[];
  gapEmployees?: AttributeEmployee[];
  metEmployees?: AttributeEmployee[];

  // Documents and files
  hasDocuments?: boolean;
  files?: Record<string, unknown>;
  comment?: string;

  // Flags
  missing?: boolean;
  needsFeedback?: boolean;
  hasTrainingCourses?: boolean;

  // Counts
  numberOfJobAttributes?: number;
  numberOfEmployeeAttributes?: number;

  // Gap analysis
  gapReason?: { label: string } | number;
}

/** Props specific to AttributeTable */
export interface AttributeTableProps extends TableProps<AttributeTableRow> {
  // Attribute-specific props
  showProficiency?: boolean;
  showStatus?: boolean;
  showCategory?: boolean;
  showExpiry?: boolean;
  showActions?: boolean;
  onAttributeClick?: (event: SyntheticEvent, attribute: AttributeTableRow) => void;
  onEditClick?: (event: SyntheticEvent, attribute: AttributeTableRow) => void;
  onDeleteClick?: (event: SyntheticEvent, attribute: AttributeTableRow) => void;
}

// ============================================================================
// Employee Table Types
// ============================================================================

/** Employee data for EmployeeTable */
export interface EmployeeTableRow extends BaseTableRow {
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  jobTitle?: string;
  department?: string;
  locationName?: string;
  status?: UserStatus | null;
  statusLabel?: string;
  isOrganizationAdmin?: boolean;
  isTeamLead?: boolean;
  isBillingUser?: boolean;
  isBilling?: boolean;
  isInstructor?: boolean;
  hasProfilePicture?: boolean;
  role?: string | boolean;
  trainingManagerConfigurationTypeName?: string;

  // Attribute counts
  employeeAttributesCount?: number;
  jobAttributesCount?: number;
  gapAttributesCount?: number;
  numberOfMetMandatoryAndRequiredAttributes?: number;
  totalNumberOfMandatoryAndRequiredAttributesForJob?: number;

  // Compliance
  isCompliant?: boolean;

  // Group settings
  groupLevelType?: number | string;
  groupNotificationType?: number | string;
  enrolments?: string;
  overdue?: string;
  isTrainingManager?: boolean;
  isFullAccess?: boolean;
}

/** Props specific to EmployeeTable */
export interface EmployeeTableProps extends TableProps<EmployeeTableRow> {
  // Employee-specific props
  showRole?: boolean;
  showStatus?: boolean;
  showLocation?: boolean;
  onEmployeeClick?: (event: SyntheticEvent, employee: EmployeeTableRow) => void;
}

/** Tree node with children expanded property */
export type TreeNodeWithChildren = GridTreeNodeWithRender & {
  childrenExpanded?: boolean;
  depth: number;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the header name for a table column, supporting translation keys.
 * If the label starts with "tableColumns.", it will be passed through the translate function.
 * @param c - The column definition
 * @param translate - Optional translation function
 * @param fallback - Optional fallback value if no label is found
 */
export function getHeaderName<T extends BaseTableRow>(
  c: TableColumnDef<T>,
  translate?: (key: string) => string,
  fallback?: string
): string {
  const label = c.label || (typeof c.type === 'object' && c.type?.label) || fallback || '';
  if (label && translate && label.startsWith('tableColumns.')) {
    return translate(label);
  }
  return label;
}

// ============================================================================
// Re-exports from MUI for convenience
// ============================================================================

export type {
  GridCallbackDetails,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
};
